import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { FileText, Inbox, Image, Users, ArrowRight, Building2, Search, AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { TrafficCard } from "../components/TrafficCard";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [seoScore, setSeoScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/admin/seo/score").then((r) => setSeoScore(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-aubergine/55 font-mono text-xs uppercase tracking-wider">Loading…</p>;

  const cards = [
    { label: "Published articles", value: stats?.articles.published ?? 0, sub: `${stats?.articles.drafts ?? 0} drafts`, to: "/admin/articles", icon: FileText, testid: "stat-articles" },
    { label: "Ecosystem members", value: stats?.members ?? 0, sub: "Active in directory", to: "/admin/members", icon: Building2, testid: "stat-members" },
    { label: "Unread messages", value: stats?.contacts.unread ?? 0, sub: `${stats?.contacts.total ?? 0} total`, to: "/admin/contacts", icon: Inbox, testid: "stat-contacts" },
    { label: "Media files", value: stats?.media ?? 0, sub: "Across folders", to: "/admin/media", icon: Image, testid: "stat-media" },
    { label: "Admins", value: stats?.admins ?? 0, sub: "On allowlist", to: "/admin/admins", icon: Users, testid: "stat-admins" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <header className="mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Overview</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-aubergine">Welcome to the <em className="accent-italic">console</em></h1>
        <p className="mt-3 text-sm text-aubergine/65 max-w-xl">Manage articles, media, admins and inbound messages from the T-REX Network site.</p>
      </header>

      {seoScore && <SEOScoreCard data={seoScore} />}

      <TrafficCard />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={c.testid} className="group rounded-lg border border-aubergine/10 bg-white p-5 hover:border-champagne/50 transition-colors">
            <div className="flex items-center justify-between">
              <c.icon className="h-4 w-4 text-aubergine/55" />
              <ArrowRight className="h-3.5 w-3.5 text-aubergine/30 group-hover:text-champagne transition-colors" />
            </div>
            <p className="mt-4 font-serif text-4xl text-aubergine">{c.value}</p>
            <p className="mt-1 text-sm font-medium text-aubergine">{c.label}</p>
            <p className="text-xs text-aubergine/55">{c.sub}</p>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-aubergine/10 bg-white p-6">
          <h2 className="font-serif text-xl text-aubergine">Quick actions</h2>
          <div className="mt-5 grid gap-3">
            <Link to="/admin/articles/new" data-testid="quick-new-article" className="flex items-center justify-between rounded-md border border-aubergine/10 px-4 py-3 hover:border-champagne/50 transition-colors">
              <div><div className="text-sm font-medium">Write new article</div><div className="text-xs text-aubergine/55">Draft or publish editorial content</div></div>
              <ArrowRight className="h-4 w-4 text-aubergine/45" />
            </Link>
            <Link to="/admin/media" data-testid="quick-upload-media" className="flex items-center justify-between rounded-md border border-aubergine/10 px-4 py-3 hover:border-champagne/50 transition-colors">
              <div><div className="text-sm font-medium">Upload media</div><div className="text-xs text-aubergine/55">Logos, photos, organized in folders</div></div>
              <ArrowRight className="h-4 w-4 text-aubergine/45" />
            </Link>
            <Link to="/admin/admins" data-testid="quick-invite-admin" className="flex items-center justify-between rounded-md border border-aubergine/10 px-4 py-3 hover:border-champagne/50 transition-colors">
              <div><div className="text-sm font-medium">Invite an admin</div><div className="text-xs text-aubergine/55">Add a teammate to the allowlist</div></div>
              <ArrowRight className="h-4 w-4 text-aubergine/45" />
            </Link>
          </div>
        </div>
        <div className="rounded-lg border border-aubergine/10 bg-aubergine text-bone p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-champ-light/80 mb-2">Live site</p>
          <h2 className="font-serif text-xl">{stats?.pages ?? 0} pages indexed</h2>
          <p className="mt-3 text-sm text-bone/70">Including static marketing pages and published articles.</p>
          <a href="/" target="_blank" rel="noreferrer" data-testid="dashboard-view-site" className="mt-5 inline-flex items-center font-mono text-[11px] uppercase tracking-[0.15em] text-champagne hover:text-champ-light">
            View public site <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Headline SEO health card. Big score ring + component bars + Fix links.
// ---------------------------------------------------------------------------
function SEOScoreCard({ data }) {
  const { score, grade, components } = data;
  const gradeColor = score >= 90 ? "text-champagne" : score >= 75 ? "text-champagne" : score >= 60 ? "text-champ-light" : "text-signal-red";
  const stroke = score >= 90 ? "#D8B37E" : score >= 75 ? "#D8B37E" : score >= 60 ? "#E8CFA1" : "#B14545";
  const dash = 2 * Math.PI * 46;
  const offset = dash * (1 - score / 100);

  return (
    <section className="mb-8 rounded-lg border border-aubergine/10 bg-white overflow-hidden" data-testid="seo-score-card">
      <div className="grid md:grid-cols-[auto_1fr] gap-6 p-6">
        <div className="flex items-center gap-6">
          <div className="relative w-28 h-28 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="46" strokeWidth="6" fill="none" stroke="rgba(45,26,54,0.08)" />
              <circle
                cx="50" cy="50" r="46" strokeWidth="6" fill="none"
                stroke={stroke}
                strokeLinecap="round"
                strokeDasharray={dash}
                strokeDashoffset={offset}
                style={{ transition: "stroke-dashoffset 800ms ease-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-serif text-3xl text-aubergine leading-none" data-testid="seo-score-value">{score}</div>
              <div className={`font-mono text-[10px] uppercase tracking-[0.15em] mt-0.5 ${gradeColor}`} data-testid="seo-score-grade">Grade {grade}</div>
            </div>
          </div>
          <div className="hidden md:block">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-1 flex items-center gap-1.5">
              <Search className="h-3 w-3" /> SEO health
            </p>
            <h2 className="font-serif text-xl text-aubergine leading-tight">
              {score >= 90 ? "Firing on all cylinders." :
                score >= 75 ? "Solid, minor tune-ups pending." :
                score >= 60 ? "Room to improve." :
                "Needs attention."}
            </h2>
            <Link
              to="/admin/seo"
              data-testid="seo-score-open"
              className="mt-2 inline-flex items-center font-mono text-[11px] uppercase tracking-[0.15em] text-aubergine/65 hover:text-champagne"
            >
              Open SEO console <ArrowRight className="ml-1 h-3 w-3" />
            </Link>
          </div>
        </div>

        <div className="grid gap-2 self-center" data-testid="seo-score-components">
          {components.map((c) => (
            <ScoreRow key={c.id} c={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ScoreRow({ c }) {
  const level = c.score >= 90 ? "ok" : c.score >= 60 ? "warn" : "bad";
  const barColor = level === "ok" ? "bg-champagne" : level === "warn" ? "bg-champ-light" : "bg-signal-red";
  const Icon = level === "ok" ? CheckCircle2 : level === "warn" ? AlertTriangle : AlertCircle;
  const iconColor = level === "ok" ? "text-champagne" : level === "warn" ? "text-champ-light" : "text-signal-red";
  return (
    <Link
      to={c.action_url + (c.action_tab ? `?tab=${c.action_tab}` : "")}
      data-testid={`seo-score-row-${c.id}`}
      className="group grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md hover:bg-bone/40 px-2 py-1.5 -mx-2 transition-colors"
    >
      <Icon className={`h-3.5 w-3.5 ${iconColor} flex-shrink-0`} />
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium text-aubergine truncate">{c.label}</div>
          <div className={`font-mono text-[10px] tabular-nums ${level === "bad" ? "text-signal-red" : "text-aubergine/55"}`} data-testid={`seo-score-${c.id}-value`}>{c.score}</div>
        </div>
        <div className="mt-1 h-1 rounded-full bg-aubergine/10 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${c.score}%`, transition: "width 800ms ease-out" }} />
        </div>
        <div className="text-[11px] text-aubergine/55 mt-1 truncate">{c.detail}</div>
      </div>
      <ArrowRight className="h-3 w-3 text-aubergine/30 group-hover:text-champagne transition-colors" />
    </Link>
  );
}
